import { useTranslation } from 'react-i18next';

import { useNavigateToDocument } from '@/hooks/documents';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import { DocumentLink } from '@/components/meta';

import { isMod } from '@imminently/decisively-ui/Kbd';
import { Typography } from '@mui/material';
import Detail from "@imminently/decisively-ui/Detail";
import DetailTable from "@imminently/decisively-ui/DetailTable";
import { GraphNode } from '@packages/commons';
import clsx from "clsx";
import Icon from "@imminently/decisively-ui/Icon";
import { getDocumentUsageItem } from "./DocumentUsage.utils";

const processSteps = (steps, id, interviewName, usage) => {
  if (!steps) return;
  steps.forEach(step => {
    if (step.layout) {
      step.layout.forEach((layout, index) => {
        if (layout.attributes && layout.attributes.indexOf(id)) usage.push(step.layout.length > 1 ? `Step "${step.title}" (Layout: ${index}) of interview "${interviewName}"` : `Step "${step.title}" of interview "${interviewName}"`)
      })
    }
    if (step.steps && step.steps.length > 0) processSteps(step.steps, id, interviewName, usage);
  })
}

const useInterviewUsage = (itemId: string) => {
  const release = useFullRelease();
  let interviewUsage: string[] = [];
  if (release && release.interviews) {
    // Attempt to find if this item is used in a screen in this interview
    release.interviews.forEach(interview => {
      processSteps(interview.steps, itemId, interview.name, interviewUsage);
    })
  }
  return interviewUsage;
};

export type DocumentUsageProps = {
  id: string;
  definedIn?: string | null;
  usedIn?: string[] | null;
  referencedIn?: string[] | null;
  showUsedIn?: boolean;
  showReferencedIn?: boolean;
  showInterviews?: boolean;
  /** If true, remove definedIn from the usedIn list */
  noDuplicates?: boolean;
  noContainer?: boolean;
  scrollable?: boolean;
  /** Return true to override default behaviour, otherwise will open the document */
  onClick?: (e: React.MouseEvent, doc: string) => boolean | void;
} & React.HTMLAttributes<HTMLTableElement | HTMLDivElement>;

export const DocumentUsage = ({
  id,
  definedIn: definedInProp,
  usedIn: usedInProp,
  referencedIn: referencedInProp,
  onClick,
  showUsedIn = true,
  showReferencedIn = true,
  showInterviews = false,
  noDuplicates = false,
  noContainer = false,
  scrollable = false,
  ...props
}: DocumentUsageProps) => {
  const navigateToDocument = useNavigateToDocument();
  const release = useFullRelease();
  const documents = release?.documents;

  const definedIn = definedInProp ? getDocumentUsageItem(documents, definedInProp) : null;
  const usedIn = usedInProp?.map((doc) => getDocumentUsageItem(documents, doc))
    // if noDuplicates is true, remove definedIn from the usedIn list
    .filter(doc => !noDuplicates || doc.identity !== definedIn?.identity);
  const referencedIn = referencedInProp?.map((doc) => getDocumentUsageItem(documents, doc))
    // if noDuplicates is true, remove definedIn from the referencedIn list
    .filter(doc => !noDuplicates || doc.identity !== definedIn?.identity);

  const interviewUsage = useInterviewUsage(id);
  const hasUsedIn = usedIn && usedIn.length > 0;
  const hasReferencedIn = referencedIn && referencedIn.length > 0;
  const displayUsedIn = showUsedIn && hasUsedIn;
  const displayReferencedIn = showReferencedIn && hasReferencedIn;
  const { t } = useTranslation();

  const onDocumentClick = (e: React.MouseEvent, doc: string) => {
    if (onClick) {
      const res = onClick(e, doc);
      // the consumer can return true to prevent the default behaviour
      if (res === true) return;
    }
    const newWindow = isMod(e);
    navigateToDocument(doc, newWindow);
  };

  const content = (
    <>
      {definedIn ? (
        <DetailTable.Item>
          <DetailTable.Icon>
            <Icon name="defined-in" />
          </DetailTable.Icon>
          <DetailTable.Title>{t('defined_in')}</DetailTable.Title>
          <DetailTable.Content>
            <DocumentLink
              variant="caption"
              onClick={(e) => onDocumentClick(e, definedIn.navigationPath)}
            >
              {definedIn.label}
            </DocumentLink>
          </DetailTable.Content>
        </DetailTable.Item>
      ) : null}
      {displayUsedIn ? (
        <DetailTable.Item>
          <DetailTable.Icon>
            <Icon name="documents" />
          </DetailTable.Icon>
          <DetailTable.Title>{t('used_in')}</DetailTable.Title>
          <DetailTable.Content>
            <div className={clsx("flex flex-col flex-1", scrollable && "overflow-y-auto max-h-24")}>
              {usedIn.map((doc, index) => (
                <DocumentLink
                  variant="caption"
                  key={`${doc.identity}-${index}`}
                  onClick={(e) => onDocumentClick(e, doc.navigationPath)}
                >
                  {doc.label}
                </DocumentLink>
              ))}
            </div>
          </DetailTable.Content>
        </DetailTable.Item>
      ) : null}
      {displayReferencedIn ? (
        <DetailTable.Item>
          <DetailTable.Icon>
            <Icon name="documents" />
          </DetailTable.Icon>
          <DetailTable.Title>{t('referenced_in')}</DetailTable.Title>
          <DetailTable.Content>
            <div className={clsx("flex flex-col flex-1", scrollable && "overflow-y-auto max-h-24")}>
              {referencedIn.map((doc, index) => (
                <DocumentLink
                  variant="caption"
                  key={`${doc.identity}-${index}`}
                  onClick={(e) => onDocumentClick(e, doc.navigationPath)}
                >
                  {doc.label}
                </DocumentLink>
              ))}
            </div>
          </DetailTable.Content>
        </DetailTable.Item>
      ) : null}
      {showInterviews && interviewUsage && interviewUsage.length > 0 ? (
        <DetailTable.Item>
          <DetailTable.Icon>
            <Icon name="interview" />
          </DetailTable.Icon>
          <DetailTable.Title>{t('interviews')}</DetailTable.Title>
          <DetailTable.Content>
            <div className={clsx("flex flex-col flex-1", scrollable && "overflow-y-auto max-h-24")}>
              {interviewUsage.map((usage) => (
                <Typography key={usage} variant="caption">
                  {usage}
                </Typography>
              ))}
            </div>
          </DetailTable.Content>
        </DetailTable.Item>
      ) : null}
    </>
  );

  if (noContainer) return content;

  return (
    <DetailTable {...props as any}>
      {content}
    </DetailTable>
  );
};

// Backward compatibility wrapper for AttributeUsage
export type AttributeUsageProps = {
  node: GraphNode;
  showUsedIn?: boolean;
  showInterviews?: boolean;
  /** If true, remove definedIn from the usedIn list */
  noDuplicates?: boolean;
  noContainer?: boolean;
  /** Return true to override default behaviour, otherwise will open the document */
  onClick?: (e: React.MouseEvent, doc: string) => boolean | void;
} & React.HTMLAttributes<HTMLTableElement | HTMLDivElement>;

/** @deprecated use DocumentUsage instead */
export const AttributeUsage = ({ node, ...props }: AttributeUsageProps) => {
  return (
    <DocumentUsage
      id={node.id}
      definedIn={node.definedIn}
      usedIn={node.usedIn}
      referencedIn={(node as any).referencedIn}
      {...props}
    />
  );
};
